import { useEffect, useReducer } from 'react';
import {
  FilterCheckbox,
  FilterConfig,
  FilterInput,
  FilterRangeUpdates,
  Filter,
  FilterConfigSlider,
  FilterType,
  FilterConfigToggleKey
} from 'types/Search';
import searchFilterReducer, { initialState, Types } from '../ducks/searchFilter';

export type Args = {
  clearRadioFilter?: (filterKey: string, pageName: string) => void;
  filters: Filter[];
  handleFilterRadioToggle?: (filterKey: string, filterConfigKey: string) => void;
  handleFilterSet?: (filterKey: string, value: string) => void;
  handleFilterSlider?: (filterKey: string, value: FilterRangeUpdates) => void;
  handleFilterToggle?: (filterKey: string, filterConfigKey: string) => void;
  page?: string;
};

const getActiveFilters = (filter: Filter): string[] => {
  switch (filter.type) {
    case FilterInput.CHECKBOX:
    case FilterInput.RADIO:
      // The active state for Sort is only set when not using the application default choice.
      if (filter.key === FilterType.SORT && 'best-match' in filter.config && filter.config['best-match'].selected) {
        return [];
      }

      return Object.values(filter.config)
        .filter(config => config.selected)
        .map(config => config.name);
    case FilterInput.SLIDER:
      return JSON.stringify(filter.config.displayValue) !== JSON.stringify(filter.config.initialRange) ? [filter.display] : [];
    case FilterInput.STAR_RATING:
      return filter.config !== null ? [filter.display] : [];
    default:
      // eslint-disable-next-line no-console
      console.warn(`getActiveFilters unexpected filter type: ${JSON.stringify(filter)}`);
      return [];
  }
};

const getAllActiveFilters = (filtersByKey: { [key: string]: Filter }): string[] =>
  Object.values(filtersByKey).reduce((active, nextFilter) => [...active, ...getActiveFilters(nextFilter)], []);

const getRangeStateUpdates = (config: FilterConfigSlider): FilterRangeUpdates => ({
  convertedRange: config.value,
  displayRange: config.displayValue,
  formattedRange: config.formattedRange
});

const saveFilterChanges = (filter: Filter, savedFilter: Filter, updateMethods: { [key: string]: (key: string, updates: any) => void }) => {
  switch (filter.type) {
    case FilterInput.CHECKBOX:
      Object.keys(filter.config).forEach((configKey: FilterConfigToggleKey) => {
        if (filter.config[configKey].selected !== (savedFilter as FilterCheckbox).config[configKey].selected) {
          updateMethods[FilterInput.CHECKBOX](filter.key, configKey);
        }
      });
      break;
    case FilterInput.RADIO:
      updateMethods[FilterInput.RADIO](
        filter.key,
        Object.keys(filter.config).find((configKey: FilterConfigToggleKey) => filter.config[configKey].selected === true)
      );
      break;
    case FilterInput.SLIDER:
      updateMethods[FilterInput.SLIDER](filter.key, getRangeStateUpdates(filter.config));
      break;
    case FilterInput.STAR_RATING:
      updateMethods[FilterInput.STAR_RATING](filter.key, filter.config);
      break;
    default:
      // eslint-disable-next-line no-console
      console.warn(`saveChanges unexpected filter type: ${JSON.stringify(filter)}`);
      break;
  }
};

const useSearchFilters = ({
  clearRadioFilter,
  filters,
  handleFilterRadioToggle,
  handleFilterSet,
  handleFilterSlider,
  handleFilterToggle,
  page
}: Args) => {
  const [state, dispatch] = useReducer(searchFilterReducer, initialState);

  const activeFilters = getAllActiveFilters(state.savedByKey);

  useEffect(() => {
    dispatch({ type: Types.UPDATE_ALL, payload: filters });
  }, [filters]);

  const clearDraftChanges = () => {
    dispatch({ type: Types.CLEAR_CHANGES });
  };

  const clearSavedFilters = () => {
    Object.values(state.savedByKey).forEach((filter: Filter) => {
      switch (filter.type) {
        case FilterInput.CHECKBOX:
          Object.keys(filter.config).forEach((configKey: FilterConfigToggleKey) => {
            // Toggle any checkbox value to off for this component.
            if (filter.config[configKey].selected === true) {
              handleFilterToggle(filter.key, configKey);
            }
          });
          break;
        case FilterInput.RADIO:
          clearRadioFilter(filter.key, page);
          break;
        case FilterInput.SLIDER:
          filter.config.displayValue = filter.config.initialRange;
          filter.config.value = [0, -1];
          handleFilterSlider(filter.key, getRangeStateUpdates(filter.config));
          break;
        case FilterInput.STAR_RATING:
          handleFilterSet(filter.key, null);
          break;
        default:
          // eslint-disable-next-line no-console
          console.warn(`clearSavedFilters unexpected filter type: ${JSON.stringify(filter)}`);
          break;
      }
    });

    dispatch({ type: Types.CLEAR_CHANGES });
  };

  const saveChanges = () => {
    Object.values(state.changes).forEach((filter: Filter) =>
      saveFilterChanges(filter, state.savedByKey[filter.key], {
        [FilterInput.CHECKBOX]: handleFilterToggle,
        [FilterInput.RADIO]: handleFilterRadioToggle,
        [FilterInput.SLIDER]: handleFilterSlider,
        [FilterInput.STAR_RATING]: handleFilterSet
      })
    );
  };

  const updateFilter = (key: string, changes: FilterConfig) => {
    dispatch({ type: Types.CHANGE_ONE, payload: { key, changes } });
  };

  const filtersByKey = {
    ...state.savedByKey,
    ...state.changes
  };

  return {
    activeFilters,
    clearDraftChanges,
    clearSavedFilters,
    filtersByKey,
    saveChanges,
    updateFilter
  };
};

export default useSearchFilters;
